import React, { useId, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

/**
 * A single accessible FAQ accordion item: a real `<button aria-expanded aria-controls>` header
 * and a `role="region" aria-labelledby` panel. Built as a lightweight custom accordion because
 * RSuite's `Panel` renders its header as a non-focusable `<div tabIndex={-1}>` (not a real
 * button), so composition alone can't produce keyboard-operable, correctly-labelled markup.
 */
const FAQItem = ({ question, defaultExpanded = false, children }) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const generatedId = useId();
    const headerId = `faq-header-${generatedId}`;
    const panelId = `faq-panel-${generatedId}`;

    const toggleExpanded = () => setExpanded((previous) => !previous);

    return (
        <div className={`faq-item ${expanded ? 'faq-item-expanded' : ''}`}>
            <h4 className="faq-item-heading">
                <button
                    type="button"
                    id={headerId}
                    className="faq-item-header"
                    aria-expanded={expanded}
                    aria-controls={panelId}
                    onClick={toggleExpanded}
                >
                    <span className="faq-item-title">{question}</span>
                    <FontAwesomeIcon
                        icon={faChevronDown}
                        className={`faq-item-caret ${expanded ? 'faq-item-caret-expanded' : ''}`}
                        aria-hidden="true"
                    />
                </button>
            </h4>
            {expanded && (
                <div id={panelId} role="region" aria-labelledby={headerId} className="faq-item-body">
                    {children}
                </div>
            )}
        </div>
    );
};

export default FAQItem;
